import { useBranchRoutes } from '@/lib/use-routes';
import { Link, createFileRoute } from '@tanstack/react-router';
import { format } from 'date-fns';
import { Store } from 'lucide-react';
import { useMemo } from 'react';

import { ListPage } from '@/components/app/list-page';
import type { ListColumn } from '@/components/app/list-page';
import { Button } from '@/components/ui/button';
import { formatMoney, useListSales } from '@/features/sales';
import {
  ExportMenu,
  downloadCsv,
} from '@/features/sales/components/pages/sales-page-ui';
import type { SaleWithRelations } from '@borradh-workspace/api-client/types';

export const Route = createFileRoute(
  '/_authed/dashboard/l/$locationId/sales/product-orders'
)({
  component: ProductOrdersPage,
});

/** One product line item, carrying the sale it was sold on. */
type ProductOrderRow = {
  item: SaleWithRelations['items'][number];
  sale: SaleWithRelations;
};

/**
 * Product orders, on the shared `ListPage`. The `ProductOrdersMobile` component
 * that restated these columns by hand is gone — both layouts render from the
 * same config.
 */
function ProductOrdersPage() {
  const routes = useBranchRoutes();
  const { sales, isLoading, isError, error } = useListSales({ limit: 200 });

  const rows = useMemo<ProductOrderRow[]>(
    () =>
      sales.flatMap((sale) =>
        sale.items
          .filter((item) => item.itemType === 'product')
          .map((item) => ({ item, sale }))
      ),
    [sales]
  );

  const handleExport = () => {
    downloadCsv(
      'product-orders.csv',
      ['Created', 'Name', 'Qty', 'Unit price', 'Total'],
      rows.map(({ item, sale }) => [
        format(new Date(sale.createdAt), 'd MMM yyyy, HH:mm'),
        item.name,
        item.quantity,
        formatMoney(item.unitPriceCents, sale.currency),
        formatMoney(item.totalCents, sale.currency),
      ])
    );
  };

  const columns: ListColumn<ProductOrderRow>[] = [
    {
      id: 'created',
      header: 'Created',
      // Under the product name on a phone — the money columns are the two the
      // phone must not stack, so the date is what fits beneath the title.
      mobile: 'secondary',
      cell: ({ sale }) => (
        <span className="text-muted-foreground">
          {format(new Date(sale.createdAt), 'd MMM yyyy, HH:mm')}
        </span>
      ),
    },
    {
      id: 'name',
      header: 'Name',
      mobile: 'primary',
      cell: ({ item }) => item.name,
    },
    {
      id: 'quantity',
      header: 'Qty',
      cell: ({ item }) => item.quantity,
    },
    {
      id: 'unitPrice',
      header: 'Unit price',
      align: 'right',
      cell: ({ item, sale }) => formatMoney(item.unitPriceCents, sale.currency),
    },
    {
      id: 'total',
      header: 'Total',
      align: 'right',
      mobile: 'trailing',
      cell: ({ item, sale }) => (
        <span className="font-medium">
          {formatMoney(item.totalCents, sale.currency)}
        </span>
      ),
    },
  ];

  // Two different "no rows" situations: an org that has taken sales but sold no
  // products, and an org whose store is not set up at all. Only the second one
  // gets a call to action.
  const hasSoldSomething = sales.length > 0;

  return (
    <>
      <title>Product Orders | Borrah</title>

      <ListPage<ProductOrderRow>
        config={{
          title: 'Product orders',
          columns,
          rows,
          rowKey: ({ item }) => item.id,
          toolbar: (
            <ExportMenu
              disabled={!rows.length}
              label="Options"
              onExportCsv={handleExport}
            />
          ),
          isLoading,
          isError,
          errorMessage: error?.message ?? "Couldn't load product orders",
          empty: hasSoldSomething
            ? {
                icon: Store,
                title: 'No product orders yet',
                description:
                  'Products sold through your sales and online store will appear here.',
              }
            : {
                icon: Store,
                title: 'Online store is not yet set up',
                description:
                  'Set up your store in minutes and get clients buying your products online.',
                action: (
                  <Button asChild className="rounded-full" variant="outline">
                    <Link to={routes.catalogProducts}>Set up now</Link>
                  </Button>
                ),
              },
        }}
      />
    </>
  );
}
